/**
 * PermissionEditor Component
 *
 * Modal for editing user permissions.
 * Allows owners to customize exactly what each team member can see and do.
 */

import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function PermissionEditor({ member, isOpen, onClose, onSave }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Permission state
  const [permissions, setPermissions] = useState({
    // Assessments
    can_create_assessments: true,
    can_view_all_assessments: true,
    can_view_own_assessments_only: false,
    can_edit_assessments: false,
    can_delete_assessments: false,
    can_share_assessments: true,

    // Customers
    can_view_customers: true,
    can_create_customers: true,
    can_edit_customers: false,
    can_delete_customers: false,

    // Team & Settings
    can_view_team: false,
    can_invite_users: false,
    can_manage_team: false,
    can_access_settings: false,
    can_manage_billing: false,

    // Reporting
    can_view_dashboard: true,
    can_view_analytics: false,
    can_export_reports: false,

    // Access Mode
    access_mode: 'office'
  })

  // Fetch current permissions when modal opens
  useEffect(() => {
    if (isOpen && member) {
      fetchPermissions()
    }
  }, [isOpen, member])

  const fetchPermissions = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', member.id)
        .single()

      if (fetchError) throw fetchError

      if (data) {
        setPermissions(data)
      }
    } catch (err) {
      console.error('Error fetching permissions:', err)
      setError('Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = (permissionName) => {
    setPermissions(prev => ({
      ...prev,
      [permissionName]: !prev[permissionName]
    }))
  }

  const handleAccessModeChange = (mode) => {
    setPermissions(prev => ({
      ...prev,
      access_mode: mode
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      const { error: updateError } = await supabase
        .from('user_permissions')
        .update(permissions)
        .eq('user_id', member.id)

      if (updateError) throw updateError

      // Call parent callback
      if (onSave) {
        await onSave()
      }

      onClose()
    } catch (err) {
      console.error('Error saving permissions:', err)
      setError('Failed to save permissions. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '15px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '5px' }}>
            Edit Permissions
          </h2>
          <p style={{ color: '#64748b', margin: 0 }}>
            {member?.full_name} ({member?.email})
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '12px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Loading permissions...
          </div>
        ) : (
          <>
            {/* Access Mode Selection */}
            <div style={{ marginBottom: '30px', padding: '20px', background: '#f8fafc', borderRadius: '8px', border: '2px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#0f172a' }}>
                🎨 Interface Mode
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button
                  onClick={() => handleAccessModeChange('office')}
                  className="btn"
                  style={{
                    background: permissions.access_mode === 'office' ? '#667eea' : 'white',
                    color: permissions.access_mode === 'office' ? 'white' : '#2c3e50',
                    border: `2px solid ${permissions.access_mode === 'office' ? '#667eea' : '#e2e8f0'}`,
                    padding: '15px',
                    fontSize: '16px'
                  }}
                >
                  💼 Office Mode
                  <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
                    Full desktop experience
                  </div>
                </button>
                <button
                  onClick={() => handleAccessModeChange('field')}
                  className="btn"
                  style={{
                    background: permissions.access_mode === 'field' ? '#667eea' : 'white',
                    color: permissions.access_mode === 'field' ? 'white' : '#2c3e50',
                    border: `2px solid ${permissions.access_mode === 'field' ? '#667eea' : '#e2e8f0'}`,
                    padding: '15px',
                    fontSize: '16px'
                  }}
                >
                  📱 Field Mode
                  <div style={{ fontSize: '12px', marginTop: '5px', opacity: 0.9 }}>
                    Simplified mobile interface
                  </div>
                </button>
              </div>
            </div>

            {/* Permission Categories */}
            <div style={{ display: 'grid', gap: '20px' }}>
              {/* Assessments */}
              <PermissionCategory
                title="📋 Assessments"
                permissions={[
                  { key: 'can_create_assessments', label: 'Create new assessments' },
                  { key: 'can_view_all_assessments', label: 'View all team assessments' },
                  { key: 'can_view_own_assessments_only', label: 'View only own assessments' },
                  { key: 'can_edit_assessments', label: 'Edit any assessment' },
                  { key: 'can_delete_assessments', label: 'Delete assessments' },
                  { key: 'can_share_assessments', label: 'Share assessments with customers' }
                ]}
                permissionState={permissions}
                onToggle={handleToggle}
              />

              {/* Customers */}
              <PermissionCategory
                title="👥 Customers"
                permissions={[
                  { key: 'can_view_customers', label: 'View customer list' },
                  { key: 'can_create_customers', label: 'Create new customers' },
                  { key: 'can_edit_customers', label: 'Edit customer information' },
                  { key: 'can_delete_customers', label: 'Delete customers' }
                ]}
                permissionState={permissions}
                onToggle={handleToggle}
              />

              {/* Team & Settings */}
              <PermissionCategory
                title="🔧 Team & Settings"
                permissions={[
                  { key: 'can_view_team', label: 'View team page' },
                  { key: 'can_invite_users', label: 'Invite new users' },
                  { key: 'can_manage_team', label: 'Manage team members (activate/deactivate)' },
                  { key: 'can_access_settings', label: 'Access settings page' },
                  { key: 'can_manage_billing', label: 'Manage billing & subscription' }
                ]}
                permissionState={permissions}
                onToggle={handleToggle}
              />

              {/* Reporting & Analytics */}
              <PermissionCategory
                title="📊 Reporting & Analytics"
                permissions={[
                  { key: 'can_view_dashboard', label: 'View dashboard' },
                  { key: 'can_view_analytics', label: 'View analytics page' },
                  { key: 'can_export_reports', label: 'Export reports (PDF, CSV)' }
                ]}
                permissionState={permissions}
                onToggle={handleToggle}
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              marginTop: '30px',
              paddingTop: '20px',
              borderTop: '2px solid #e5e7eb',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={onClose}
                className="btn btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Helper component for permission categories
function PermissionCategory({ title, permissions, permissionState, onToggle }) {
  return (
    <div style={{
      padding: '20px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '2px solid #e2e8f0'
    }}>
      <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#0f172a' }}>
        {title}
      </h3>
      <div style={{ display: 'grid', gap: '12px' }}>
        {permissions.map(({ key, label }) => (
          <label
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '10px',
              background: 'white',
              borderRadius: '6px',
              border: '2px solid #e2e8f0',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#667eea'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0'
            }}
          >
            <input
              type="checkbox"
              checked={permissionState[key]}
              onChange={() => onToggle(key)}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                accentColor: '#667eea'
              }}
            />
            <span style={{ fontSize: '14px', color: '#1e293b' }}>{label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
